import React from 'react';

/**
 * Base class for various diagrams, containing the key helper methods
 * and the G component, enabling the specific diagram classes to be
 * almost entirely the diagram JSX code generated by parse.mjs.
 */
export class Diagram extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            // Relationship data, read from file
            allConnections: {},

            // Current selected label (null = none)
            currentSelection: null
        };
    }

    componentDidMount() {
        this.parseConnectionData();
    }

    /** 
     * Checks a layermember attribute string to see if any of the layers is 
     * active.
     */
    isAnyLayerActive = (layermemberStr) => {
        if (layermemberStr) {
            // Parse the semicolon-separated string
            const layerIds = layermemberStr.split(';');

            // For each layer
            for (const layerId of layerIds) {
                // Get the layer number
                const layerNum = parseInt(layerId);

                if (this.props.activeLayers.has(layerNum)) {
                    // It's explicitly designated as active
                    return true;
                }

                const checkboxLayer = this.props.layers.find((layer) => layer.number === layerNum);
                if (!checkboxLayer) {
                    // It's not associated with a checkbox, so it's always on
                    return true;
                }
            }
        }

        return false;
    };

    /** Sets the description key and value.
     * 
     * @param event        Mouse event
     * @param label        The description key, which is the box label.
     * @param description  The description value, which is the detailed definition.
     */
    setDescription = (event, label, description) => {
        // Don't propagate since <g> tags are nested
        event.stopPropagation();
        
        if (label === undefined || description === undefined) {
            label = undefined;
            description = undefined;
        }
        this.props.setDescription(label, description);
    }

    /** Gets the current connections for a box. */
    getConnections = (label) => {
        let current = [];
        
        if (label) {
            label = label.trim().toLowerCase();
            const connections = this.state.allConnections[label];
            if (connections) {
                // All layers
                let layers = Object.keys(connections);
    
                // Intersection with active layers
                layers = layers.filter((layer) => this.props.activeLayers.has(parseInt(layer, 10))); 
    
                layers.forEach((layer) => {
                    current = current.concat(connections[layer].to);
                    current = current.concat(connections[layer].from)
                });
            }
    
            current = [ ...new Set(current) ];
        }

        return current;
    }

    /** Sets the current selection. */
    setCurrentSelection = (label) => {
        if (label) {
            label = label.trim().toLowerCase();
        }
        this.setState({ currentSelection: label });
    };

    /** Tests whether a label matches the current selection. */
    isCurrentSelection = (label) => {
        if (label) {
            label = label.trim().toLowerCase();
        }
        return (label === this.state.currentSelection);
    };

    handleClick = (event, label, description) => {
        if (label) {
            // Clicked on box

            if (this.isCurrentSelection(label)) {
                // Already selected, so de-select
                this.setDescription(event, null, null);
                this.setCurrentSelection(null);
            }
            else {
                // Select the box
                this.setDescription(event, label, description);
                this.setCurrentSelection(label);
            }
        }
    };

    /** 
     * Functional component that either gives the original <g> tag and 
     * its children, or nothing if all the component's layers are inactive.
     */
    G = (props) => {
        // Determine the current connections, based on selection and active layers
        let currentConnections = this.getConnections(this.state.currentSelection);

        let layermemberStr = props.layermember;
        if (layermemberStr === undefined || this.isAnyLayerActive(layermemberStr)) {
            // In an active layer
            
            // Set up any style embellishments

            let style = {};

            let label = '';
            if (props.hasOwnProperty('label')) {
                label = props.label;

                if (label) {
                    label = label.trim().toLowerCase();
                    if (this.state.currentSelection === label) {
                        style.fill = 'LightBlue';
                    }
                    else if (currentConnections.includes(label)) {
                        style.fill = 'Yellow';
                    }
                }
            }

            // Give the original tag, with style and click embellishments
            return (
                <g {...props} style={style} onClick={(event) => this.handleClick(event, props.label, props.description)}>
                    {props.children}
                </g>
            );
        }
        else {
            // In an inactive layer 
            return null;
        }
    }
}